<template>
  <div class="date-range" :class="[variant ? 'date-range--' + variant : '', { 'is-disabled': disabled, 'date-range--single': single }]">
    <div ref="anchor" class="date-range-anchor">
      <div class="date-range-shell" :class="{ 'is-open': activePicker !== null, 'date-range-shell--combined': combined && !single }">
        <div class="dr-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>

        <!-- Combined mode: From and To are still both shown, but the whole
             pill is a SINGLE button — clicking anywhere opens one unified
             start→end flow (the two-month popup below). No separate "To"
             entry point that closes after a single click. -->
        <button
          v-if="combined && !single"
          type="button"
          class="dr-field dr-field--combined"
          :class="{ 'is-active': activePicker !== null }"
          :disabled="disabled"
          @click="openPicker('from')"
        >
          <span class="dr-combined-part" :class="{ 'is-empty': !(value && value.fromDate) }">
            <span class="dr-label">{{ fromLabel }}</span>
            <span class="dr-display">{{ formatDisplay(value && value.fromDate) }}</span>
          </span>

          <span class="dr-sep" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="13 6 19 12 13 18" />
            </svg>
          </span>

          <span class="dr-combined-part" :class="{ 'is-empty': !(value && value.toDate) }">
            <span class="dr-label">{{ toLabel }}</span>
            <span class="dr-display">{{ formatDisplay(value && value.toDate) }}</span>
          </span>
        </button>

        <template v-else>
          <button
            type="button"
            class="dr-field"
            :class="{ 'is-active': activePicker === 'from', 'is-empty': !(value && value.fromDate) }"
            :disabled="disabled"
            @click="openPicker('from')"
          >
            <span v-if="!single" class="dr-label">{{ fromLabel }}</span>
            <span class="dr-display">{{ formatDisplay(value && value.fromDate) }}</span>
          </button>

          <template v-if="!single">
            <div class="dr-sep" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="13 6 19 12 13 18" />
              </svg>
            </div>

            <button
              type="button"
              class="dr-field"
              :class="{ 'is-active': activePicker === 'to', 'is-empty': !(value && value.toDate) }"
              :disabled="disabled"
              @click="openPicker('to')"
            >
              <span class="dr-label">{{ toLabel }}</span>
              <span class="dr-display">{{ formatDisplay(value && value.toDate) }}</span>
            </button>
          </template>
        </template>
      </div>

      <transition name="dr-fade">
        <div
          v-if="activePicker !== null"
          ref="popup"
          class="dr-popup"
          :class="{ 'dr-popup--multi': !single && viewMode === 'days' }"
          :style="popupStyle"
          role="dialog"
        >
          <!-- Top header: shown for single-date mode and for the month /
               year pickers. Range mode's day view uses the two-month layout
               below, where each month carries its own header. -->
          <div v-if="single || viewMode !== 'days'" class="cal-header">
            <button type="button" class="cal-nav" @click="shiftMonth(-1)" aria-label="Previous month">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <div class="cal-title-group">
              <button type="button" class="cal-title" @click="cycleViewMode">
                {{ viewModeLabel }}
              </button>
            </div>

            <button type="button" class="cal-nav" @click="shiftMonth(1)" aria-label="Next month">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          <template v-if="viewMode === 'days'">
            <!-- Single-date mode: one month. -->
            <div v-if="single" class="cal-grid cal-days" @mouseleave="clearHover">
              <div v-for="(dow, di) in WEEKDAYS" :key="`dow-${di}`" class="cal-dow">{{ dow }}</div>
              <button
                v-for="(day, i) in calendarDays"
                :key="`d-${i}`"
                type="button"
                class="cal-cell"
                :class="dayClass(day)"
                :disabled="day.isDisabled"
                @click="selectDate(day)"
                @mouseenter="onDayHover(day)"
              >
                {{ day.day }}
              </button>
            </div>

            <!-- Range mode: two months side by side (flight-app style) so a
                 from→to range can be chosen in one go, without reopening. -->
            <div v-else class="cal-multi" @mouseleave="clearHover">
              <div class="cal-month">
                <div class="cal-header">
                  <button type="button" class="cal-nav" @click="shiftMonth(-1)" aria-label="Previous month">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>
                  <div class="cal-title-group">
                    <button type="button" class="cal-title" @click="cycleViewMode">{{ firstMonthLabel }}</button>
                  </div>
                  <span class="cal-nav-spacer" aria-hidden="true"></span>
                </div>
                <div class="cal-grid cal-days">
                  <div v-for="(dow, di) in WEEKDAYS" :key="`fdow-${di}`" class="cal-dow">{{ dow }}</div>
                  <button
                    v-for="(day, i) in calendarDays"
                    :key="`fd-${i}`"
                    type="button"
                    class="cal-cell"
                    :class="dayClass(day)"
                    :disabled="day.isDisabled"
                    @click="selectDate(day)"
                    @mouseenter="onDayHover(day)"
                  >
                    {{ day.day }}
                  </button>
                </div>
              </div>

              <div class="cal-month">
                <div class="cal-header">
                  <span class="cal-nav-spacer" aria-hidden="true"></span>
                  <div class="cal-title-group">
                    <button type="button" class="cal-title" @click="cycleViewMode">{{ secondMonthLabel }}</button>
                  </div>
                  <button type="button" class="cal-nav" @click="shiftMonth(1)" aria-label="Next month">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
                <div class="cal-grid cal-days">
                  <div v-for="(dow, di) in WEEKDAYS" :key="`sdow-${di}`" class="cal-dow">{{ dow }}</div>
                  <button
                    v-for="(day, i) in secondCalendarDays"
                    :key="`sd-${i}`"
                    type="button"
                    class="cal-cell"
                    :class="dayClass(day)"
                    :disabled="day.isDisabled"
                    @click="selectDate(day)"
                    @mouseenter="onDayHover(day)"
                  >
                    {{ day.day }}
                  </button>
                </div>
              </div>
            </div>
          </template>

          <!-- Month picker -->
          <div v-else-if="viewMode === 'months'" class="cal-grid cal-months">
            <button
              v-for="(m, i) in MONTHS_SHORT"
              :key="`m-${i}`"
              type="button"
              class="cal-cell cal-period"
              :class="{ 'is-selected': i === viewMonth }"
              @click="selectMonth(i)"
            >
              {{ m }}
            </button>
          </div>

          <!-- Year picker -->
          <div v-else class="cal-grid cal-years">
            <button
              v-for="y in yearGrid"
              :key="`y-${y}`"
              type="button"
              class="cal-cell cal-period"
              :class="{ 'is-selected': y === viewYear }"
              @click="selectYear(y)"
            >
              {{ y }}
            </button>
          </div>

          <div class="cal-footer" :class="{ 'cal-footer--presets': showPopupPresets }">
            <template v-if="showPopupPresets">
              <div class="cal-foot-presets">
                <button
                  v-for="preset in resolvedPresets"
                  :key="preset.key"
                  type="button"
                  class="dr-chip"
                  :class="{ 'is-active': preset.key === activePreset }"
                  @click="applyPreset(preset)"
                >
                  {{ preset.label }}
                </button>
              </div>
              <button type="button" class="cal-foot-btn cal-foot-btn--ghost" @click="closePicker">Close</button>
            </template>
            <template v-else>
              <button type="button" class="cal-foot-btn" @click="setToday">Today</button>
              <button type="button" class="cal-foot-btn cal-foot-btn--ghost" @click="closePicker">Close</button>
            </template>
          </div>
        </div>
      </transition>
    </div>

    <div v-if="resolvedPresets.length && !presetsInPopup" class="dr-presets">
      <button
        v-for="preset in resolvedPresets"
        :key="preset.key"
        type="button"
        class="dr-chip"
        :class="{ 'is-active': preset.key === activePreset }"
        :disabled="disabled"
        @click="applyPreset(preset)"
      >
        {{ preset.label }}
      </button>
    </div>
  </div>
</template>

<script>
const DEFAULT_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 Days' },
  { key: '30d', label: 'Last 30 Days' },
  { key: 'mtd', label: 'This Month' },
  { key: 'last-month', label: 'Last Month' }
]

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function toDateInput (date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function rangeForKey (key) {
  const today = new Date()
  const start = new Date()
  const end = new Date()

  switch (key) {
    case 'today':
      break
    case 'yesterday':
      start.setDate(today.getDate() - 1)
      end.setDate(today.getDate() - 1)
      break
    case '7d':
      start.setDate(today.getDate() - 6)
      break
    case '30d':
      start.setDate(today.getDate() - 29)
      break
    case 'mtd':
      start.setDate(1)
      break
    case 'last-month':
      start.setMonth(today.getMonth() - 1, 1)
      end.setMonth(today.getMonth(), 0)
      break
    default:
      return null
  }

  return { fromDate: toDateInput(start), toDate: toDateInput(end) }
}

export default {
  name: 'DateRangePicker',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: Object,
      default: () => ({ fromDate: '', toDate: '' })
    },
    presets: {
      type: [Array, Boolean],
      default: () => DEFAULT_PRESETS
    },
    fromLabel: { type: String, default: 'From' },
    toLabel: { type: String, default: 'To' },
    // Combined mode: keep both From / To fields visible but make the whole
    // pill a single trigger — picking always flows start→end in one two-month
    // popup. Ignored in `single` mode.
    combined: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    // Visual variant. '' = default pill chip; 'form' = compact 36px-tall
    // rectangular shell that fits inside form grids alongside text inputs
    // and AppDropdown triggers.
    variant: { type: String, default: '' },
    // Single-date mode — hides the to-picker, separator, range constraints,
    // and presets. Wrapped by SingleDatePicker.vue for a string v-model.
    single: { type: Boolean, default: false },
    // Opt-in: render the preset chips INSIDE the calendar popup footer (beside
    // Close) instead of as a row below the field. Used by the Orders search
    // filter so the always-36px pill doesn't stretch its 4-column grid row.
    // Defaults off — every other usage keeps the below-field chip row.
    presetsInPopup: { type: Boolean, default: false }
  },
  data () {
    const today = new Date()
    return {
      activePreset: '',
      activePicker: null,
      // Range mode is a two-step pick: false → next click sets the start,
      // true → next click sets the end. hoverIso drives the live preview
      // of the span while the end is being chosen.
      pickingEnd: false,
      hoverIso: '',
      viewYear: today.getFullYear(),
      viewMonth: today.getMonth(),
      viewMode: 'days',
      // Calendar popup is rendered with position:fixed and absolute viewport
      // coords so it escapes any ancestor with `overflow: hidden` (the
      // Orders page section card was clipping it). Recomputed on open and
      // on scroll/resize while open.
      popupStyle: {},
      WEEKDAYS,
      MONTHS_SHORT
    }
  },
  computed: {
    resolvedPresets () {
      if (this.single) return []
      if (this.presets === false) return []
      if (Array.isArray(this.presets)) return this.presets
      return DEFAULT_PRESETS
    },
    // Chips live in the popup footer only when explicitly opted in AND there
    // are presets to show — otherwise the footer keeps its Today/Close pair.
    showPopupPresets () {
      return this.presetsInPopup && this.resolvedPresets.length > 0
    },
    viewModeLabel () {
      if (this.viewMode === 'years') {
        const start = Math.floor(this.viewYear / 12) * 12
        return `${start} – ${start + 11}`
      }
      if (this.viewMode === 'months') return `${this.viewYear}`
      return `${MONTHS_LONG[this.viewMonth]} ${this.viewYear}`
    },
    calendarDays () {
      return this.daysForMonth(this.viewYear, this.viewMonth)
    },
    // Range mode renders a second month immediately after the first.
    secondMonthMeta () {
      const total = this.viewYear * 12 + this.viewMonth + 1
      return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 }
    },
    secondCalendarDays () {
      return this.daysForMonth(this.secondMonthMeta.year, this.secondMonthMeta.month)
    },
    firstMonthLabel () {
      return `${MONTHS_LONG[this.viewMonth]} ${this.viewYear}`
    },
    secondMonthLabel () {
      const m = this.secondMonthMeta
      return `${MONTHS_LONG[m.month]} ${m.year}`
    },
    yearGrid () {
      const start = Math.floor(this.viewYear / 12) * 12
      return Array.from({ length: 12 }, (_, i) => start + i)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (next) {
        this.activePreset = this.detectPreset(next)
      }
    },
    viewMode () {
      // The popup is wider in the two-month day view than in the month /
      // year pickers — reposition so it stays inside the viewport.
      if (this.activePicker !== null) {
        this.$nextTick(this.recomputePopupPosition)
      }
    }
  },
  beforeDestroy () {
    document.removeEventListener('mousedown', this.handleOutside)
    document.removeEventListener('keydown', this.handleKey)
    window.removeEventListener('scroll', this.recomputePopupPosition, true)
    window.removeEventListener('resize', this.recomputePopupPosition)
  },
  methods: {
    formatDisplay (iso) {
      if (!iso) return 'Select date'
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return iso
      const day = String(d.getDate()).padStart(2, '0')
      return `${day} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`
    },
    openPicker (field) {
      if (this.disabled) return
      this.activePicker = field
      this.viewMode = 'days'
      if (!this.single) {
        // Clicking "From" starts a fresh range; clicking "To" (when a start
        // already exists) jumps straight to choosing the end.
        this.pickingEnd = field === 'to' && !!(this.value && this.value.fromDate)
        this.hoverIso = ''
      }
      const target = field === 'from'
        ? (this.value && this.value.fromDate)
        : (this.value && this.value.toDate)
      const ref = target ? new Date(target) : new Date()
      this.viewYear = ref.getFullYear()
      this.viewMonth = ref.getMonth()
      this.$nextTick(() => {
        this.recomputePopupPosition()
        document.addEventListener('mousedown', this.handleOutside)
        document.addEventListener('keydown', this.handleKey)
        window.addEventListener('scroll', this.recomputePopupPosition, true)
        window.addEventListener('resize', this.recomputePopupPosition)
      })
    },
    closePicker () {
      this.activePicker = null
      this.pickingEnd = false
      this.hoverIso = ''
      document.removeEventListener('mousedown', this.handleOutside)
      document.removeEventListener('keydown', this.handleKey)
      window.removeEventListener('scroll', this.recomputePopupPosition, true)
      window.removeEventListener('resize', this.recomputePopupPosition)
    },
    recomputePopupPosition () {
      const anchor = this.$refs.anchor
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      // Measure the rendered popup — its width differs between the two-month
      // day view and the narrower month / year pickers. Fall back to the
      // single-month size before the first render.
      const popup = this.$refs.popup
      const popupWidth = (popup && popup.offsetWidth) || 304
      const popupHeight = (popup && popup.offsetHeight) || 340
      const margin = 8
      const viewportW = window.innerWidth
      const viewportH = window.innerHeight

      // Default: drop below the anchor, left-aligned to it.
      let top = rect.bottom + margin
      let left = rect.left

      // Flip above if there's not enough room below but there is above —
      // typical when the field sits near the bottom of the viewport.
      if (top + popupHeight > viewportH - margin && rect.top - popupHeight - margin >= margin) {
        top = rect.top - popupHeight - margin
      }

      // Keep the popup inside the viewport horizontally.
      if (left + popupWidth > viewportW - margin) {
        left = Math.max(margin, viewportW - popupWidth - margin)
      }
      if (left < margin) left = margin

      this.popupStyle = {
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`
      }
    },
    handleOutside (event) {
      const root = this.$el
      if (root && !root.contains(event.target)) {
        this.closePicker()
      }
    },
    handleKey (event) {
      if (event.key === 'Escape') this.closePicker()
    },
    cycleViewMode () {
      if (this.viewMode === 'days') this.viewMode = 'months'
      else if (this.viewMode === 'months') this.viewMode = 'years'
      else this.viewMode = 'days'
    },
    shiftMonth (delta) {
      if (this.viewMode === 'years') {
        this.viewYear += delta * 12
        return
      }
      if (this.viewMode === 'months') {
        this.viewYear += delta
        return
      }
      const total = this.viewYear * 12 + this.viewMonth + delta
      this.viewYear = Math.floor(total / 12)
      this.viewMonth = ((total % 12) + 12) % 12
    },
    selectMonth (i) {
      this.viewMonth = i
      this.viewMode = 'days'
    },
    selectYear (y) {
      this.viewYear = y
      this.viewMode = 'months'
    },
    setToday () {
      const today = new Date()
      this.viewYear = today.getFullYear()
      this.viewMonth = today.getMonth()
      this.viewMode = 'days'
      this.pickDate(toDateInput(today))
    },
    daysForMonth (year, month) {
      const days = []
      const firstOfMonth = new Date(year, month, 1)
      const startDayOfWeek = firstOfMonth.getDay()
      const start = new Date(firstOfMonth)
      start.setDate(1 - startDayOfWeek)

      for (let i = 0; i < 42; i++) {
        const d = new Date(start)
        d.setDate(start.getDate() + i)
        days.push(this.dayMeta(d, month))
      }
      return days
    },
    dayMeta (d, refMonth) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const iso = toDateInput(d)
      const fromIso = this.value && this.value.fromDate
      const toIso = this.value && this.value.toDate

      const isFromSelected = !!fromIso && iso === fromIso
      const isToSelected = !!toIso && iso === toIso

      // While choosing the end of a range, preview the span up to the hovered
      // day so the calendar reads like a flight-booking picker.
      let rangeEnd = toIso
      let isToPreview = false
      if (!this.single && this.pickingEnd && this.hoverIso && fromIso && this.hoverIso > fromIso) {
        rangeEnd = this.hoverIso
        isToPreview = iso === this.hoverIso
      }
      const isInRange = !!(fromIso && rangeEnd && iso > fromIso && iso < rangeEnd)

      return {
        date: d,
        iso,
        day: d.getDate(),
        isCurrentMonth: d.getMonth() === refMonth,
        isToday: iso === toDateInput(today),
        isFromSelected,
        isToSelected,
        isSelected: isFromSelected || isToSelected,
        isInRange,
        isToPreview,
        isDisabled: false
      }
    },
    dayClass (day) {
      const v = this.value || {}
      // A one-day range (from === to) stays fully rounded rather than getting
      // the squared-off endpoints used to bridge a multi-day span.
      const sameDay = !!v.fromDate && v.fromDate === v.toDate
      return {
        'is-other-month': !day.isCurrentMonth,
        'is-today': day.isToday,
        'is-selected': day.isSelected,
        'is-from': day.isFromSelected && !sameDay,
        'is-to': day.isToSelected && !sameDay,
        'is-in-range': day.isInRange,
        'is-to-preview': day.isToPreview && !day.isSelected,
        'is-disabled': day.isDisabled
      }
    },
    onDayHover (day) {
      if (this.single || !this.pickingEnd) return
      this.hoverIso = day.isDisabled ? '' : day.iso
    },
    clearHover () {
      this.hoverIso = ''
    },
    selectDate (day) {
      if (day.isDisabled) return
      this.pickDate(day.iso)
    },
    pickDate (iso) {
      // Single-date mode: set the one date and close.
      if (this.single) {
        const next = { ...(this.value || {}) }
        next.fromDate = iso
        this.activePreset = this.detectPreset(next)
        this.$emit('input', next)
        this.$emit('change', next)
        this.closePicker()
        return
      }

      // Range mode, flight-app flow:
      //  • first pick sets a one-day range (from === to) and waits for the end
      //  • second pick on/after the start sets the end and closes
      //  • a pick before the current start restarts the range there
      // A one-day range (rather than an empty `toDate`) keeps the value valid
      // even if the user closes after a single click.
      if (!this.pickingEnd) {
        const next = { fromDate: iso, toDate: iso }
        this.activePreset = this.detectPreset(next)
        this.activePicker = 'to'
        this.pickingEnd = true
        this.hoverIso = ''
        this.$emit('input', next)
        this.$emit('change', next)
        return
      }

      const fromIso = (this.value && this.value.fromDate) || iso
      if (iso < fromIso) {
        const next = { fromDate: iso, toDate: iso }
        this.activePreset = this.detectPreset(next)
        this.activePicker = 'to'
        this.pickingEnd = true
        this.$emit('input', next)
        this.$emit('change', next)
        return
      }

      const next = { fromDate: fromIso, toDate: iso }
      this.activePreset = this.detectPreset(next)
      this.pickingEnd = false
      this.hoverIso = ''
      this.$emit('input', next)
      this.$emit('change', next)
      this.closePicker()
    },
    applyPreset (preset) {
      const range = rangeForKey(preset.key)
      if (!range) return
      this.activePreset = preset.key
      this.$emit('input', range)
      this.$emit('change', range)
      this.$emit('preset', preset.key)
    },
    detectPreset (range) {
      if (!range || !range.fromDate || !range.toDate) return ''
      for (const p of this.resolvedPresets) {
        const candidate = rangeForKey(p.key)
        if (candidate && candidate.fromDate === range.fromDate && candidate.toDate === range.toDate) {
          return p.key
        }
      }
      return ''
    }
  }
}
</script>

<style scoped>
.date-range {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

.date-range-anchor {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

.date-range-shell {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.3rem 0.5rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  width: fit-content;
  max-width: 100%;
}

.date-range-shell:hover {
  border-color: #cbd5e1;
}

.date-range-shell.is-open {
  border-color: var(--brand-color, #0e5b46);
  box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.15);
}

.dr-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--brand-color, #0e5b46);
  flex-shrink: 0;
  padding: 0 0.15rem 0 0.3rem;
}

.dr-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.25rem 0.7rem;
  min-width: 0;
  border: none;
  background: transparent;
  border-radius: 999px;
  cursor: pointer;
  outline: none;
  transition: background-color 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.dr-field:hover:not(:disabled) {
  background: #f1f5f9;
}

.dr-field.is-active {
  background: rgba(14, 91, 70, 0.10);
}

.dr-field:focus-visible {
  box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.20);
}

.dr-field:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.dr-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  line-height: 1;
  margin-bottom: 0.15rem;
}

.dr-display {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.1;
  white-space: nowrap;
}

.dr-field.is-empty .dr-display {
  color: #94a3b8;
  font-weight: 500;
}

/* Combined mode keeps the From / To fields side by side inside ONE button,
   so the whole pill is a single click/hover target. The button lays its two
   parts out in a row (each part keeps the stacked label-over-value look). */
.dr-field--combined {
  flex-direction: row;
  align-items: center;
  gap: 0.15rem;
  padding: 0.25rem 0.4rem;
}

.dr-combined-part {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0 0.3rem;
  min-width: 0;
}

.dr-combined-part.is-empty .dr-display {
  color: #94a3b8;
  font-weight: 500;
}

.dr-field--combined .dr-sep {
  margin: 0 0.1rem;
}

/* In combined mode the whole pill is one target: hovering anywhere (icon or
   fields) highlights the entire shell, and the inner button drops its own
   rounded highlight so it never looks like a partial button. */
.date-range-shell--combined {
  cursor: pointer;
}

.date-range-shell--combined:hover:not(.is-open) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.date-range-shell--combined .dr-field--combined:hover:not(:disabled),
.date-range-shell--combined .dr-field--combined.is-active {
  background: transparent;
}

.dr-sep {
  display: inline-flex;
  align-items: center;
  color: #cbd5e1;
  flex-shrink: 0;
}

/* ---------- Calendar popup ---------- */
/* `position: fixed` + viewport coords (set inline by recomputePopupPosition)
   so the popup escapes any ancestor with `overflow: hidden` — section cards,
   modals, scroll containers etc. would otherwise clip it. */
.dr-popup {
  position: fixed;
  z-index: 1050;
  width: 304px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 14px;
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.16), 0 4px 10px rgba(15, 23, 42, 0.06);
  padding: 0.7rem;
  user-select: none;
}

/* Range mode shows two months, so let the popup grow to fit them
   (shrink-to-fit, since it's position: fixed without a right offset). */
.dr-popup--multi {
  width: auto;
}

.dr-fade-enter-active,
.dr-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dr-fade-enter,
.dr-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.cal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.cal-title-group {
  flex: 1;
  display: flex;
  justify-content: center;
}

.cal-title {
  border: none;
  background: transparent;
  padding: 0.35rem 0.7rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.88rem;
  color: #0f172a;
  cursor: pointer;
  outline: none;
  transition: background-color 0.15s ease;
}

.cal-title:hover {
  background: #f1f5f9;
}

.cal-nav {
  width: 30px;
  height: 30px;
  border: none;
  background: #f8fafc;
  border-radius: 8px;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: none;
  transition: all 0.15s ease;
}

.cal-nav:hover {
  background: var(--brand-gradient, linear-gradient(135deg, #0a4a3a 0%, #0e5b46 55%, #0a4a3a 100%));
  color: #ffffff;
}

.cal-grid {
  display: grid;
}

.cal-days {
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

/* Two-month range layout */
.cal-multi {
  display: flex;
  gap: 0.85rem;
}

.cal-month {
  width: 252px;
  max-width: 100%;
}

/* Invisible placeholder balancing a lone nav arrow so each month's title
   stays centred in its own header. */
.cal-nav-spacer {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
}

.cal-months,
.cal-years {
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  padding: 0.4rem 0.2rem;
}

.cal-dow {
  text-align: center;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #94a3b8;
  text-transform: uppercase;
  padding: 0.35rem 0;
}

.cal-cell {
  border: none;
  background: transparent;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 500;
  color: #1f2937;
  border-radius: 8px;
  cursor: pointer;
  outline: none;
  position: relative;
  transition: all 0.12s ease;
  -webkit-tap-highlight-color: transparent;
}

.cal-cell:hover:not(:disabled):not(.is-selected) {
  background: #eef2f7;
  color: #0f172a;
}

.cal-cell.is-other-month {
  color: #cbd5e1;
}

.cal-cell.is-today:not(.is-selected) {
  color: var(--brand-color, #0e5b46);
  font-weight: 700;
}

.cal-cell.is-today:not(.is-selected)::after {
  content: '';
  position: absolute;
  bottom: 4px;
  left: 50%;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--brand-color, #0e5b46);
  transform: translateX(-50%);
}

.cal-cell.is-in-range {
  background: rgba(14, 91, 70, 0.10);
  color: var(--brand-color, #0e5b46);
  border-radius: 0;
}

/* Tentative range end under the cursor while picking the second date. */
.cal-cell.is-to-preview {
  background: rgba(14, 91, 70, 0.16);
  color: var(--brand-color, #0e5b46);
  font-weight: 700;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.cal-cell.is-selected {
  background: var(--brand-gradient, linear-gradient(135deg, #0a4a3a 0%, #0e5b46 55%, #0a4a3a 100%));
  color: #ffffff;
  font-weight: 700;
  box-shadow: var(--brand-shadow, 0 4px 10px rgba(10, 74, 58, 0.25));
}

.cal-cell.is-from {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.cal-cell.is-to {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.cal-cell.is-disabled {
  color: #e2e8f0;
  cursor: not-allowed;
  background: transparent;
}

.cal-period {
  height: 44px;
  font-size: 0.85rem;
  font-weight: 600;
}

.cal-period.is-selected {
  background: var(--brand-gradient, linear-gradient(135deg, #0a4a3a 0%, #0e5b46 55%, #0a4a3a 100%));
  color: #ffffff;
}

.cal-footer {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #f1f5f9;
}

.cal-foot-btn {
  flex: 1;
  border: none;
  background: var(--brand-gradient, linear-gradient(135deg, #0a4a3a 0%, #0e5b46 55%, #0a4a3a 100%));
  color: #ffffff;
  font-weight: 600;
  font-size: 0.78rem;
  padding: 0.45rem 0.6rem;
  border-radius: 999px;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
}

.cal-foot-btn:hover {
  filter: brightness(1.05);
  box-shadow: var(--brand-shadow, 0 4px 10px rgba(10, 74, 58, 0.25));
}

.cal-foot-btn--ghost {
  background: transparent;
  color: #475569;
  border: 1px solid #e2e8f0;
  box-shadow: none;
}

.cal-foot-btn--ghost:hover {
  background: #f1f5f9;
  color: #0f172a;
  filter: none;
  box-shadow: none;
}

/* Footer that carries the preset chips (presetsInPopup): chips grouped on the
   left, Close kept to its natural width on the right. */
.cal-footer--presets {
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.cal-foot-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  flex: 1 1 auto;
}

/* Slightly more compact than the below-field chips so four fit the popup. */
.cal-foot-presets .dr-chip {
  padding: 0.28rem 0.6rem;
  font-size: 0.72rem;
}

.cal-footer--presets .cal-foot-btn--ghost {
  flex: 0 0 auto;
}

/* ---------- Preset chips ---------- */
.dr-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  position: relative;
  padding-left: 0.6rem;
}

.dr-presets::before {
  content: '';
  position: absolute;
  left: 0;
  top: 20%;
  bottom: 20%;
  width: 1px;
  background: #e2e8f0;
}

.dr-chip {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  transition: all 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.dr-chip:hover:not(:disabled):not(.is-active) {
  border-color: #cbd5e1;
  background: #f8fafc;
  color: #1f2937;
}

.dr-chip:focus-visible {
  box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.20);
}

.dr-chip.is-active {
  background: var(--brand-gradient, linear-gradient(135deg, #0a4a3a 0%, #0e5b46 55%, #0a4a3a 100%));
  border-color: transparent;
  color: #ffffff;
  box-shadow: var(--brand-shadow, 0 4px 10px rgba(10, 74, 58, 0.25));
}

.dr-chip:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.is-disabled {
  opacity: 0.7;
  pointer-events: none;
}

@media (max-width: 767px) {
  .dr-presets {
    width: 100%;
    padding-left: 0;
  }

  .dr-presets::before {
    display: none;
  }
}

@media (max-width: 575px) {
  .date-range-shell {
    width: 100%;
    border-radius: 14px;
    flex-wrap: wrap;
    padding: 0.5rem 0.6rem;
  }

  .dr-popup {
    width: calc(100vw - 32px);
    max-width: 320px;
  }

  /* Stack the two months on narrow screens — side by side won't fit. */
  .cal-multi {
    flex-direction: column;
    gap: 0.4rem;
  }

  .cal-month {
    width: 100%;
  }

  .dr-sep {
    display: none;
  }
}

/* ---------------------------------------------------------------------------
   Variant: "form"
   Opt-in compact look that matches 36px text inputs / AppDropdown triggers
   inside form grids (e.g. Orders search filters). Pass `variant="form"`.
   --------------------------------------------------------------------------- */
.date-range--form {
  display: flex;
  align-items: center;
  width: 100%;
}

.date-range--form .date-range-anchor {
  display: block;
  width: 100%;
}

.date-range--form .date-range-shell {
  display: flex;
  width: 100%;
  height: 36px;
  padding: 0 0.65rem;
  gap: 0.4rem;
  border-radius: 8px;
  border-color: #d8dde6;
  box-shadow: none;
}

.date-range--form .date-range-shell:hover {
  border-color: #b9c2d0;
}

.date-range--form .date-range-shell.is-open {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.date-range--form .dr-icon {
  color: #16a07a;
  padding: 0;
}

.date-range--form .dr-field {
  flex-direction: row;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  padding: 0 0.4rem;
  border-radius: 6px;
  height: 26px;
}

.date-range--form .dr-label {
  display: none;
}

.date-range--form .dr-display {
  font-size: 0.85rem;
  font-weight: 500;
  color: #2f3640;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
}

.date-range--form .dr-field.is-empty .dr-display {
  color: #9ca3af;
  font-weight: 500;
}

.date-range--form .dr-field.is-active {
  background: rgba(22, 160, 122, 0.10);
}

.date-range--form .dr-sep {
  color: #9ca3af;
}

.date-range--form .dr-presets {
  margin-top: 0.5rem;
  padding-left: 0;
  width: 100%;
}

.date-range--form .dr-presets::before {
  display: none;
}

@media (max-width: 575px) {
  .date-range--form .date-range-shell {
    border-radius: 8px;
    flex-wrap: nowrap;
    padding: 0 0.65rem;
  }

  .date-range--form .dr-sep {
    display: inline-flex;
  }
}
</style>
